import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader, SyncButton, AdminEmptyState, ResponsiveTableWrap } from "@/components/admin/crud-helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/branch-staff")({
  component: BranchStaffPage,
});

type Branch = { id: string; name: string; code: string };
type StaffRow = { id: string; branch_id: string; user_id: string; role: string; created_at: string };
type Profile = { id: string; display_name: string | null; avatar_url: string | null };

const ROLES = ["manager", "supervisor", "staff", "cashier", "support"];

function BranchStaffPage() {
  const qc = useQueryClient();
  const [branchId, setBranchId] = useState<string>("all");
  const [search, setSearch] = useState("");

  const branches = useQuery({
    queryKey: ["branches_min"],
    queryFn: async () => {
      const { data, error } = await supabase.from("branches").select("id, name, code").order("name");
      if (error) throw error;
      return (data ?? []) as Branch[];
    },
  });

  const staff = useQuery({
    queryKey: ["all_branch_staff"],
    queryFn: async () => {
      const { data, error } = await supabase.from("branch_staff").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as StaffRow[];
    },
  });

  const userIds = useMemo(() => Array.from(new Set((staff.data ?? []).map(s => s.user_id))), [staff.data]);
  const profiles = useQuery({
    queryKey: ["staff_profiles", userIds.sort().join(",")],
    queryFn: async () => {
      if (!userIds.length) return [] as Profile[];
      const { data, error } = await supabase.from("profiles").select("id, display_name, avatar_url").in("id", userIds);
      if (error) throw error;
      return (data ?? []) as Profile[];
    },
  });
  const profileMap = useMemo(() => {
    const m = new Map<string, Profile>();
    for (const p of profiles.data ?? []) m.set(p.id, p);
    return m;
  }, [profiles.data]);
  const branchMap = useMemo(() => {
    const m = new Map<string, Branch>();
    for (const b of branches.data ?? []) m.set(b.id, b);
    return m;
  }, [branches.data]);

  const filtered = useMemo(() => {
    const rows = staff.data ?? [];
    return rows.filter(r => {
      if (branchId !== "all" && r.branch_id !== branchId) return false;
      if (!search.trim()) return true;
      const p = profileMap.get(r.user_id);
      const b = branchMap.get(r.branch_id);
      const hay = `${p?.display_name ?? ""} ${r.user_id} ${b?.name ?? ""} ${b?.code ?? ""} ${r.role}`.toLowerCase();
      return hay.includes(search.toLowerCase());
    });
  }, [staff.data, branchId, search, profileMap, branchMap]);

  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of staff.data ?? []) m.set(s.branch_id, (m.get(s.branch_id) ?? 0) + 1);
    return m;
  }, [staff.data]);

  const remove = async (id: string) => {
    if (!confirm("Remove this staff assignment?")) return;
    const { error } = await supabase.from("branch_staff").delete().eq("id", id);
    if (error) toast.error(error.message);
    else { toast.success("Removed"); qc.invalidateQueries({ queryKey: ["all_branch_staff"] }); }
  };

  const updateRole = async (id: string, role: string) => {
    const { error } = await supabase.from("branch_staff").update({ role }).eq("id", id);
    if (error) toast.error(error.message);
    else qc.invalidateQueries({ queryKey: ["all_branch_staff"] });
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Branch staff"
        description="Assign and remove users from branches and franchises."
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name, user ID, branch…"
        count={filtered.length}
        actions={
          <>
            <SyncButton onSync={() => staff.refetch()} loading={staff.isFetching} />
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/branches">Branches</Link>
            </Button>
          </>
        }
      />

      <AssignCard branches={branches.data ?? []} onAssigned={() => qc.invalidateQueries({ queryKey: ["all_branch_staff"] })} />

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
        <button
          type="button"
          onClick={() => setBranchId("all")}
          className={
            "rounded-lg border p-2 text-left transition " +
            (branchId === "all" ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/40")
          }
        >
          <div className="text-xs uppercase tracking-wide text-muted-foreground">All</div>
          <div className="mt-1 text-sm font-medium">Every branch</div>
          <div className="mt-0.5 text-xs text-muted-foreground">{(staff.data ?? []).length} staff</div>
        </button>
        {(branches.data ?? []).map(b => (
          <button
            key={b.id}
            type="button"
            onClick={() => setBranchId(b.id)}
            className={
              "rounded-lg border p-2 text-left transition " +
              (branchId === b.id ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/40")
            }
          >
            <div className="font-mono text-xs text-muted-foreground">{b.code}</div>
            <div className="mt-0.5 truncate text-sm font-medium">{b.name}</div>
            <div className="mt-0.5 text-xs text-muted-foreground">{counts.get(b.id) ?? 0} staff</div>
          </button>
        ))}
      </div>

      {!staff.isLoading && filtered.length === 0 ? (
        <AdminEmptyState title={search || branchId !== "all" ? "No staff match" : "No staff assignments yet"} />
      ) : (
        <div className="rounded-lg border border-border bg-card">
          <ResponsiveTableWrap>
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-xs">
                <tr>
                  <th className="px-4 py-2 text-left">User</th>
                  <th className="hidden px-4 py-2 text-left md:table-cell">Branch</th>
                  <th className="px-4 py-2 text-left">Role</th>
                  <th className="hidden px-4 py-2 text-left lg:table-cell">Added</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {staff.isLoading && <tr><td colSpan={5} className="px-4 py-6 text-muted-foreground">Loading…</td></tr>}
                {filtered.map(s => {
                  const p = profileMap.get(s.user_id);
                  const b = branchMap.get(s.branch_id);
                  return (
                    <tr key={s.id} className="border-t border-border hover:bg-secondary/40">
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          {p?.avatar_url
                            ? <img src={p.avatar_url} alt="" className="h-7 w-7 shrink-0 rounded-full" />
                            : <div className="h-7 w-7 shrink-0 rounded-full bg-secondary" />}
                          <div className="min-w-0">
                            <div className="truncate font-medium">{p?.display_name ?? "—"}</div>
                            <div className="font-mono text-xs text-muted-foreground">
                              {s.user_id.slice(0, 8)}…
                              <span className="md:hidden"> · {b?.code ?? "—"}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="hidden px-4 py-2 md:table-cell">
                        {b ? <>{b.name} <Badge variant="outline" className="ml-1">{b.code}</Badge></> : "—"}
                      </td>
                      <td className="px-4 py-2">
                        <Select value={s.role} onValueChange={(v) => updateRole(s.id, v)}>
                          <SelectTrigger className="h-8 w-[140px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="hidden px-4 py-2 text-muted-foreground lg:table-cell">{new Date(s.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-2 text-right">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => remove(s.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ResponsiveTableWrap>
        </div>
      )}
    </div>
  );
}

function AssignCard({ branches, onAssigned }: { branches: Branch[]; onAssigned: () => void }) {
  const [branchId, setBranchId] = useState("");
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState("staff");
  const [busy, setBusy] = useState(false);

  // search profiles
  const [query, setQuery] = useState("");
  const search = useQuery({
    queryKey: ["profile_search", query],
    enabled: query.trim().length > 1,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, display_name, avatar_url")
        .ilike("display_name", `%${query.trim()}%`)
        .limit(8);
      if (error) throw error;
      return (data ?? []) as Profile[];
    },
  });

  const assign = async () => {
    if (!branchId || !userId.trim()) { toast.error("Branch and user required"); return; }
    setBusy(true);
    const { error } = await supabase.from("branch_staff").insert({ branch_id: branchId, user_id: userId.trim(), role });
    setBusy(false);
    if (error) toast.error(error.message);
    else { toast.success("Assigned"); setUserId(""); setQuery(""); onAssigned(); }
  };

  return (
    <Card>
      <CardContent className="p-4 grid gap-3 md:grid-cols-[1fr_2fr_140px_auto]">
        <div className="grid gap-2">
          <Label>Branch</Label>
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
            <SelectContent>
              {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name} ({b.code})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2 relative">
          <Label>User</Label>
          <Input
            placeholder="User ID or search by name"
            value={userId || query}
            onChange={e => { setUserId(""); setQuery(e.target.value); }}
          />
          {query.trim().length > 1 && !userId && (search.data ?? []).length > 0 && (
            <div className="absolute z-10 top-full mt-1 w-full bg-popover border border-border rounded-md shadow max-h-60 overflow-auto">
              {(search.data ?? []).map(p => (
                <button
                  key={p.id}
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-secondary flex items-center gap-2"
                  onClick={() => { setUserId(p.id); setQuery(p.display_name ?? p.id); }}
                >
                  {p.avatar_url
                    ? <img src={p.avatar_url} alt="" className="h-6 w-6 rounded-full" />
                    : <div className="h-6 w-6 rounded-full bg-secondary" />}
                  <span className="text-sm">{p.display_name ?? p.id.slice(0, 8)}</span>
                  <span className="ml-auto text-xs text-muted-foreground font-mono">{p.id.slice(0, 8)}…</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="grid gap-2">
          <Label>Role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button onClick={assign} disabled={busy}>
            <UserPlus className="mr-2 h-4 w-4" /> Assign
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
